<script setup lang="ts">
import { computed } from "vue";
import { UiButton, UiIcon, UiSelect, UiText } from "@shopexperts/shared";
import type { BanRule } from "@shopexperts/legacy-api";
import BanListSearchInput from "./BanListSearchInput.vue";
import BanListEmptyState from "./BanListEmptyState.vue";
import BanListLoadMore from "./BanListLoadMore.vue";
import BanRuleCard from "./BanRuleCard.vue";
import type { BanRuleActiveFilter } from "@/app/composables/ban-list/useBanListPage";

const props = defineProps<{
    rules: BanRule[];
    typeOptions: { label: string; value: string }[];
    isLoading?: boolean;
    isFetching?: boolean;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    hasFilters?: boolean;
    isBusy?: boolean;
    summary: { total: number; active: number; hits: number; hasMore: boolean };
}>();

const emit = defineEmits<{
    (e: "create"): void;
    (e: "edit", rule: BanRule): void;
    (e: "toggle-active", rule: BanRule): void;
    (e: "delete", rule: BanRule): void;
    (e: "show-emails", rule: BanRule): void;
    (e: "show-ips", rule: BanRule): void;
    (e: "test", rule: BanRule): void;
    (e: "load-more"): void;
    (e: "reset-filters"): void;
}>();

const searchInput = defineModel<string>("searchInput", { required: true });
const typeFilter = defineModel<string>("typeFilter", { required: true });
const activeFilter = defineModel<BanRuleActiveFilter>("activeFilter", { required: true });

const activeFilterOptions: readonly { label: string; value: BanRuleActiveFilter }[] = [
    { label: "All rules", value: "all" },
    { label: "Active only", value: "active" },
    { label: "Inactive only", value: "inactive" },
];

const typeSelectOptions = computed(() => [
    { label: "All types", value: "" },
    ...props.typeOptions,
]);

const summaryLabel = computed(() => {
    const { total, active, hits, hasMore } = props.summary;
    if (total === 0) return "";
    const scope = hasMore ? `first ${total} rules` : `${total} ${total === 1 ? "rule" : "rules"}`;
    return `Showing ${scope} · ${active} active · ${hits.toLocaleString()} total hits across them`;
});
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <BanListSearchInput v-model="searchInput" placeholder="Search name or terms..." />

                <div class="w-full sm:w-[170px]">
                    <UiSelect v-model="typeFilter" :options="typeSelectOptions" placeholder="All types" size="md" />
                </div>

                <div class="w-full sm:w-[170px]">
                    <UiSelect
                        v-model="activeFilter"
                        :options="(activeFilterOptions as unknown as { label: string; value: string }[])"
                        placeholder="All rules"
                        size="md"
                    />
                </div>

                <UiButton
                    v-if="hasFilters"
                    variant="ghost"
                    label="Clear filters"
                    @click="emit('reset-filters')"
                />
            </div>

            <UiButton variant="primary" label="Add Rule" @click="emit('create')">
                <template #before_label>
                    <UiIcon name="plus-square" class="h-5 w-5 text-se-primary" />
                </template>
            </UiButton>
        </div>

        <UiText
            v-if="summaryLabel && !isLoading"
            as="p"
            variant="caption-normal"
            class="text-se-gray-500"
            :text="summaryLabel"
        />

        <div v-if="isLoading" class="py-12 text-center">
            <UiText as="p" variant="p" class="text-se-gray-600" text="Loading ban rules..." />
        </div>

        <BanListEmptyState
            v-else-if="rules.length === 0"
            icon="shield-tick"
            :title="hasFilters ? 'No rules match these filters' : 'No ban rules yet'"
            :description="hasFilters
                ? 'Try a different term, type or status — or clear the filters.'
                : 'A rule is one bannable offence: a list of terms that must all appear in the same field for a submission to be blocked.'"
            :action-label="hasFilters ? undefined : 'Add Rule'"
            @action="emit('create')"
        />

        <template v-else>
            <BanRuleCard
                v-for="rule in rules"
                :key="rule.id"
                :rule="rule"
                :is-busy="isBusy"
                @edit="emit('edit', rule)"
                @toggle-active="emit('toggle-active', rule)"
                @delete="emit('delete', rule)"
                @show-emails="emit('show-emails', rule)"
                @show-ips="emit('show-ips', rule)"
                @test="emit('test', rule)"
            />

            <BanListLoadMore
                :has-next-page="hasNextPage"
                :is-fetching-next-page="isFetchingNextPage"
                :is-refreshing="isFetching && !isLoading && !isFetchingNextPage"
                label="Load more rules"
                @load-more="emit('load-more')"
            />
        </template>
    </div>
</template>
